import { FilterWithCheckboxList } from '@borg/pim-components'
import { CircularProgress, Tooltip, Typography } from '@mui/material'
import React, { useState, type FunctionComponent } from 'react'
import { getVisibleActiveIntegrations, trackAnalyticEvent, ANALYTICS_LOCATIONS } from '../../utils'
import {
	ActiveIntegration,
	CardGrid,
	IntegrationsFilters,
	LearnButtonContent,
	ItemInfoIcon,
	SortBySelect,
	SORT_OPTIONS,
} from '../'
import { sortActiveIntegrationsByDate, sortActiveIntegrationsByName, sortActiveIntegrationsByStatus } from '../../utils/sorting'
import { useActiveIntegrationsFeeds } from '../../hooks'
import { extractNameFiltersFromIntegrations } from './utils'
import { ActiveIntegrationsEmptyStateWrapper, ActiveIntegrationsWrapper } from './active-integrations.styles'

const ActiveIntegrationsEmptyState: FunctionComponent<{ message: string, color?: 'error' | 'inherit' }> = ( { message, color = 'inherit' } ) => (
	<ActiveIntegrationsEmptyStateWrapper>
		<Typography variant={'body2'} color={color} align={'center'}>
			{message}
		</Typography>
	</ActiveIntegrationsEmptyStateWrapper>
)

export const ActiveIntegrations: FunctionComponent = () => {
	const { integrations, isLoading, isError } = useActiveIntegrationsFeeds()
	const [ searchValue, setSearchValue ] = useState<string | null>( null )
	const [ filterValues, setFilterValues ] = useState<string[]>( [] )
	const [ sortValue, setSortValue ] = useState<string | null>( '' )
	const [ pillValue, setPillValue ] = useState<string>( 'All' )
	const integrationFilters = extractNameFiltersFromIntegrations( integrations )
	const { visibleIntegrations: integrationsToShow, allCount, needsAttentionCount } =
		getVisibleActiveIntegrations( integrations, {
			searchValue,
			filterValues,
			pillValue: pillValue === 'Needs Attention' ? 'Needs Attention' : 'All',
		} )

	const getSortedIntegrations = ( integrationsToSort: typeof integrations ) => {
		switch ( sortValue ) {
		case SORT_OPTIONS.NAME:
			return sortActiveIntegrationsByName( integrationsToSort )
		case SORT_OPTIONS.LAST_UPDATE:
			return sortActiveIntegrationsByDate( integrationsToSort, 'lastUpdate' )
		case SORT_OPTIONS.OLDEST_UPDATE:
			return sortActiveIntegrationsByDate( integrationsToSort, 'lastUpdate', true )
		case SORT_OPTIONS.STATUS:
			return sortActiveIntegrationsByStatus( integrationsToSort )
		default:
			return integrationsToSort
		}
	}

	const shownIntegrations = getSortedIntegrations( integrationsToShow )

	const shouldShowNoIntegrationsMessage = !isLoading && !isError && !integrations.length

	return (
		<ActiveIntegrationsWrapper>
			<Typography variant={'h3'}>Active Integrations</Typography>
			<IntegrationsFilters
				onSearchChange={setSearchValue}
				onSearchStart={() => {
					trackAnalyticEvent(
						ANALYTICS_LOCATIONS.ACTIVE_INTEGRATIONS,
						'Use',
						'Search Bar',
					)
				}}
				sort={<SortBySelect
					value={sortValue}
					onChange={( newSortValue: string | null ) => {
						setSortValue( newSortValue )

						trackAnalyticEvent(
							ANALYTICS_LOCATIONS.ACTIVE_INTEGRATIONS,
							'Use',
							'Sort By',
							{ sortedBy: newSortValue },
						)
					}}
				/>}
				learn={<LearnButtonContent analyticsLocation={ANALYTICS_LOCATIONS.ACTIVE_INTEGRATIONS} />}
				filters={(
					<FilterWithCheckboxList
						label={'Integrations'}
						items={integrationFilters}
						isLoading={isLoading}
						value={filterValues}
						onChange={( selectedManufacturers ) => {
							setFilterValues( selectedManufacturers )

							trackAnalyticEvent(
								ANALYTICS_LOCATIONS.ACTIVE_INTEGRATIONS,
								'Use',
								'Integration Filter',
								{ selectedManufacturers: selectedManufacturers.join( ', ' ) },
							)
						}}
						adornment={( { item } ) => Boolean( item.aliases?.length ) && (
							<Tooltip
								title={`${item.name}. Also called: ${item.aliases?.join( ', ' )}`}
							>
								<ItemInfoIcon />
							</Tooltip>
						)
						}
					/>
				)}
				onPillClick={( value: string ) => {
					trackAnalyticEvent(
						ANALYTICS_LOCATIONS.ACTIVE_INTEGRATIONS,
						'click',
						`${value} Pill/Quick Filter`,
					)

					setPillValue( value )
				}}
				pills={[
					{
						label: 'All',
						count: allCount,
						selected: pillValue === 'All',
					},
					{
						label: 'Needs Attention',
						count: needsAttentionCount,
						selected: pillValue === 'Needs Attention',
					},
				]}
			/>
			{ isLoading && <CircularProgress /> }
			{ isError && <ActiveIntegrationsEmptyState message={'Failed to load active integrations. Please try reloading the page. If the problem persists, please contact support.'} color={'error'} /> }
			{ shouldShowNoIntegrationsMessage && <ActiveIntegrationsEmptyState message={'No active integrations. Add one below.'} /> }
			{ !shouldShowNoIntegrationsMessage && !isError && !isLoading && !integrationsToShow.length && <ActiveIntegrationsEmptyState message={'No active integrations match the current filters or search.'} /> }
			{ !isLoading && !isError && shownIntegrations.length > 0 && (
				<CardGrid>
					{ shownIntegrations.map( ( activeIntegration ) => (
						<ActiveIntegration
							key={activeIntegration.feedId}
							activeIntegration={activeIntegration}
						/>
					) )}
				</CardGrid> )}
		</ActiveIntegrationsWrapper>
	)
}
